/**
 * AuthExpiredModal — модалка при истёкшей сессии (Phase 2.6).
 *
 * Логика:
 * - Открывается когда authStore.expired === true.
 * - Не закрывается без успешного входа (ни Esc, ни backdrop, ни X —
 *   иначе пользователь снова увидит 401 при следующем клике).
 * - Содержит inline email+password форму + dev-bypass кнопку (если
 *   VITE_DEV_AUTH_BYPASS_TOKEN задан).
 * - При успехе: setAuth() → модалка исчезает (expired=false) →
 *   invalidate React Query кеш → пользователь видит свежие данные.
 * - При ошибке логина — показываем extractApiError в красной карточке,
 *   НЕ закрываем модалку.
 *
 * Не показывается на /login (api.ts 401-handler не ставит expired
 * если pathname.startsWith('/login')).
 *
 * Не делает автоматический retry исходного 401-запроса — ТЗ §3
 * явно требует чтобы пользователь сам повторил действие.
 */
import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Sparkles, LogIn, ShieldAlert } from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import { api } from '@/lib/api';
import { extractApiError } from '@/lib/apiError';
import { isDevBypassEnabled, devBypassLogin } from '@/lib/devBypass';
import { useAuthStore } from '@/store/authStore';
import type { Account } from '@/hooks/useAccount';

export function AuthExpiredModal() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const expired = useAuthStore((s) => s.expired);

  const [email, setEmail] = useState('janni.kremm@gmail.com');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleBypass = async () => {
    setError(null);
    setLoading(true);
    try {
      const acc = await devBypassLogin();
      if (acc) {
        // Invalidate все queries: после login данные могут отличаться
        // (новый account_id → другой tenant). Это согласуется с тем что
        // AuthGuard при следующем рендере вызовет useProjectBootstrap.
        await qc.invalidateQueries();
        // markExpired() очищает флаг через setAuth, но дополнительно
        // вызываем clearExpired на случай если setAuth не сработал.
        useAuthStore.getState().clearExpired();
      } else {
        setError(t('auth.expired.error_invalid'));
      }
    } catch {
      setError(t('auth.expired.error_network'));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const r = await api.post<{ access_token: string; refresh_token: string; token_type: string }>(
        '/auth/login',
        { email, password },
      );
      // Подтянем профиль
      const me = await api.get<Account>('/account/me');
      useAuthStore.getState().setAuth(me.data, r.data.access_token);
      await qc.invalidateQueries();
      useAuthStore.getState().clearExpired();
    } catch (err) {
      const msg = extractApiError(err);
      setError(msg || t('auth.expired.error_invalid'));
    } finally {
      setLoading(false);
    }
  };

  // Не даём закрыть модалку без успешного входа.
  // Modal всё равно вызывает onClose на Esc/backdrop, но мы просто
  // no-op — флаг expired остаётся true → модалка остаётся видимой.
  const noopClose = () => {
    /* intentionally no-op: см. docblock */
  };

  return (
    <Modal open={expired} onClose={noopClose} title={null} maxWidth="md">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-glow-warning">
          <ShieldAlert className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-surface-100">
            {t('auth.expired.title')}
          </h2>
          <p className="text-xs text-surface-400 mt-0.5">
            {t('auth.expired.subtitle')}
          </p>
        </div>
      </div>

      {isDevBypassEnabled() && (
        <>
          <button
            type="button"
            onClick={handleBypass}
            disabled={loading}
            data-testid="auth-expired-bypass"
            className="btn-primary w-full flex items-center justify-center gap-2 mb-3"
          >
            <Sparkles className="w-4 h-4" />
            {loading
              ? t('auth.expired.submitting')
              : t('auth.expired.bypass_button')}
          </button>
          <div className="relative my-3 text-center text-xs text-surface-500">
            <span className="bg-surface-900 px-2 relative z-10">
              {t('auth.expired.or')}
            </span>
            <div className="absolute inset-x-0 top-1/2 h-px bg-surface-800" />
          </div>
        </>
      )}

      <form onSubmit={handleEmailLogin} className="space-y-3">
        <div>
          <label className="text-xs text-surface-400 mb-1 block">
            {t('auth.expired.email_label')}
          </label>
          <input
            type="email"
            className="input w-full"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            data-testid="auth-expired-email"
          />
        </div>
        <div>
          <label className="text-xs text-surface-400 mb-1 block">
            {t('auth.expired.password_label')}
          </label>
          <input
            type="password"
            className="input w-full"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="auth-expired-password"
          />
        </div>
        {error && (
          <div
            className="text-sm text-danger p-2 rounded-lg bg-danger-tint/10 border border-danger-tint/20"
            data-testid="auth-expired-error"
          >
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="auth-expired-submit"
          className="btn-ghost w-full flex items-center justify-center gap-2"
        >
          <LogIn className="w-4 h-4" />
          {loading ? t('auth.expired.submitting') : t('auth.expired.submit')}
        </button>
      </form>
    </Modal>
  );
}
